import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';

/**
 * AuthCallbackPage handles OAuth and magic link redirects from Supabase.
 *
 * This page processes the authentication tokens from the URL hash/params
 * and redirects the user to the appropriate destination.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the hash from the URL (Supabase puts tokens there)
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        );
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // Also check query params for some auth flows
        const queryParams = new URLSearchParams(window.location.search);
        const errorParam = queryParams.get('error');
        const errorDescription = queryParams.get('error_description');

        // Handle errors from OAuth provider
        if (errorParam) {
          setError(errorDescription || errorParam);
          return;
        }

        // Handle magic link or OAuth callback
        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            setError(sessionError.message);
            return;
          }
        }

        // Handle email verification callback
        if (type === 'signup' || type === 'email_change') {
          // Session should be set automatically by Supabase
          // Just verify we have a session
          const { data: { session } } = await supabase.auth.getSession();

          if (!session) {
            // Try to exchange the code if present
            const code = queryParams.get('code');
            if (code) {
              const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
              if (exchangeError) {
                setError(exchangeError.message);
                return;
              }
            }
          }
        }

        // Handle password recovery
        if (type === 'recovery') {
          // Redirect to password update page
          navigate({ to: '/reset-password' });
          return;
        }

        // Clear any stored plan from sessionStorage
        // (we'll use it on the /app route to determine redirect)

        // Redirect to the app - AuthGate will handle routing based on subscription
        navigate({ to: '/app' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="mx-4 w-full max-w-md rounded-modal border bg-card p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sapling-error-100/10">
            <svg
              className="h-6 w-6 text-sapling-error-100"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="font-display text-xl font-semibold">
            Authentication Error
          </h1>
          <p className="mt-2 text-muted-foreground">{error}</p>
          <button
            onClick={() => navigate({ to: '/' })}
            className="mt-6 inline-flex items-center justify-center rounded-btn bg-sapling-primary-100 px-4 py-2 text-sm font-medium text-white hover:bg-sapling-primary-200 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-sapling-primary-100" />
        <p className="mt-4 text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
